/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-floating-promises, @typescript-eslint/no-unsafe-argument, react-hooks/exhaustive-deps, @typescript-eslint/no-explicit-any, @typescript-eslint/require-await, @typescript-eslint/no-misused-promises, @typescript-eslint/strict-boolean-expressions */

import { alpha, useTheme } from '@mui/material/styles';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Line,
  Tooltip,
  Legend,
  ReferenceLine,
  Label,
  Brush,
  ComposedChart,
  Area,
  Dot,
} from 'recharts';
import { ErrorContent, Loader } from '../../shared';
import { DEFAULT_CHART_MARGINS, DEFAULT_Y_AXIS_STYLES } from 'config';
import { currentStatsState } from 'states/iVol';
import {
  dayjs,
  DAY_IN_MS,
  businessDaysAdd,
  formatAsPercentage,
  getFormattedDateFromUTC,
  getQueryDate,
  roundToStartOfDay,
} from 'util/shared';
import poll from 'util/poll';
import { type Dayjs } from 'dayjs';
import {
  DailyGreeks,
  ExpirationsDisplayType,
  RawGreeksDataMap,
  RawStatsData,
  RawStatsDataMap,
  TermStructure,
} from 'types/impliedVol';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import useExerciseStyles from 'hooks/iVol/useExerciseStyles';
import {
  closestExpirationIdx,
  extractTermStructureData,
  findClosestGreeksExp,
  findD50Price,
  getAtmStatsEntry,
  getFilteredGreeksData,
  getNextAvailableColor,
  getPricesPerTradeDateMap,
  getSnapshotTime,
  getTermStructureKeyFromLabel,
  getTradeDateDisplayText,
  mergeObjectLists,
} from 'util/iVol';
import { workerState } from 'states/shared';
import useBrushZoom from 'hooks/useBrushZoom';
import { DTE_TICK_CONFIG } from 'util/shared/chart';
import { TIMESTAMP_TICK_CONFIG } from 'util/shared/chart';
import { getTicksBrushed } from 'util/shared/chart';
import { getZoomConfigRefArea } from 'util/shared/chart';
import { updateBrushZoomConfig } from 'util/shared/chart';
import ChartWatermarkContainer from 'components/shared/ChartWatermarkContainer';
import { timezoneState, userDashAccessState } from 'states';
import { Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import {
  DashAccess,
  ETFSymbols,
  Earnings,
  EconomicCalendarData,
  ProductType,
  STAT_IDX,
  hasDashAccess,
} from 'types';
import useHomeContent from 'hooks/home/useHomeContent';
import useMacroCalendar from 'hooks/home/useMacroCalendar';
import useAuth from 'hooks/auth/useAuth';
import useClosingPrices from 'hooks/equityhub/useClosingPrices';
import { useTermStructureCtx } from './TermStructureContext';
import type { ExerciseStylesRecord } from 'util/moviefoneExerciseStyles';
import { useDividends } from 'hooks/useDividends';
import { transformDividendsToRiskEngineFormat } from 'util/dividendUtils';

// Rows must be keyed by tradeDate.format('YYYY-MM-DD') - a mismatch silently renders zero points.
const resolveTermStructureForLine = (
  dailyGreeks: DailyGreeks,
  rawGreeks: RawGreeksDataMap | null,
  tradeDate: Dayjs,
  timeFrame: string,
  stats: RawStatsData | null | undefined,
  currentPrice: number,
  useSpotGammaIV: boolean,
  dividendDates: Float64Array,
  dividendAmounts: Float64Array,
  exerciseStyles: ExerciseStylesRecord | null,
): TermStructure[] | null => {
  if (dailyGreeks.precomputedTermStructure != null) {
    const expectedKey = tradeDate.format('YYYY-MM-DD');
    const hasExpectedKey = dailyGreeks.precomputedTermStructure.every(
      (row) => row[expectedKey] !== undefined,
    );
    if (!hasExpectedKey) {
      console.error(
        `TermStructureChart: precomputedTermStructure for "${dailyGreeks.key}" has no values keyed "${expectedKey}" - this line will render with zero points.`,
      );
    }
    if (stats == null) {
      return dailyGreeks.precomputedTermStructure;
    }
    return dailyGreeks.precomputedTermStructure.map((row) => {
      const entry = getAtmStatsEntry(
        stats,
        row.expirationDate - tradeDate.valueOf(),
      );
      return entry == null
        ? row
        : {
            ...row,
            [`bounds-${timeFrame}`]: [entry[STAT_IDX.P10], entry[STAT_IDX.P90]],
          };
    });
  }

  if (rawGreeks == null || stats == null) {
    return null;
  }

  return extractTermStructureData(
    rawGreeks,
    tradeDate,
    timeFrame,
    stats,
    currentPrice,
    useSpotGammaIV,
    dividendDates,
    dividendAmounts,
    STAT_IDX.P10,
    STAT_IDX.P90,
    exerciseStyles,
  );
};

// Flags a value as interpolated so dots only render at real data points.
const interpolatedFlagKey = (key: string) => `${key}__interpolated`;

// Interpolates between each series' nearest real neighbors; never extrapolates past its own real range.
const interpolateMissingSeriesValues = (
  data: TermStructure[],
  sharedKey: 'daysToExpiry' | 'expirationDate',
  seriesKeys: string[],
): TermStructure[] =>
  data.map((row, index) => {
    const filled: TermStructure = { ...row };
    seriesKeys.forEach((key) => {
      if (filled[key] != null) {
        return;
      }
      let beforeIdx = index - 1;
      while (beforeIdx >= 0 && data[beforeIdx][key] == null) {
        beforeIdx -= 1;
      }
      let afterIdx = index + 1;
      while (afterIdx < data.length && data[afterIdx][key] == null) {
        afterIdx += 1;
      }
      if (beforeIdx < 0 || afterIdx >= data.length) {
        return;
      }
      const x0 = data[beforeIdx][sharedKey];
      const x1 = data[afterIdx][sharedKey];
      const y0 = data[beforeIdx][key];
      const y1 = data[afterIdx][key];
      const fraction = x1 === x0 ? 0 : (row[sharedKey] - x0) / (x1 - x0);
      filled[key] = y0 + (y1 - y0) * fraction;
      filled[interpolatedFlagKey(key)] = true;
    });
    return filled;
  });

const TermStructureChart = () => {
  const {
    selectedSym,
    config,
    updateConfig: onConfigChange,
    selectedDates: termStructureSelectedDailyGreeks,
    setSelectedDates: setTermStructureSelectedDailyGreeks,
    zoomConfig,
    setZoomConfig,
    chartData: termStructureChartData,
    setChartData,
    loading,
    setLoading,
    error,
    setError,
    chartRef,
  } = useTermStructureCtx();
  const rechartsRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getMacroCalendar } = useMacroCalendar();
  const { getEarningsForSyms } = useHomeContent();
  const worker = useRecoilValue(workerState);
  const currentTimezone = useRecoilValue(timezoneState);
  const dashAccess = useRecoilValue(userDashAccessState);

  // Internal chart-only state
  const [termStructureData, setTermStructureData] = useState<
    TermStructure[] | undefined
  >(undefined);
  const [termStructureUsedLineColors, setTermStructureUsedLineColors] =
    useState<string[]>([]);
  const [usEconomicEvents, setUsEconomicEvents] = useState<Map<
    string,
    string[]
  > | null>(null);
  const [filteredGreeksMap, setFilteredGreeksMap] =
    useState<RawGreeksDataMap | null>(null);

  // Shared stats data (kept in Recoil — fetched once, shared with other iVol components)
  const [currentStats, setCurrentStats] = useRecoilState(currentStatsState);

  const { hasAccessToProduct } = useAuth();
  const hasIVolAccess = hasAccessToProduct(ProductType.IMPLIED_VOL);

  const { getCurrentGreeksData, getDailyGreeksData, getStatisticsData } =
    useImpliedVolatility();

  const exerciseStyles = useExerciseStyles(selectedSym);

  const { getClosingPrice } = useClosingPrices();

  const availableColors = Object.values(theme.palette.iVol.termStructure);

  // Derive display settings from config
  const {
    displayType: selectedDisplayType,
    showEconomicEvents,
    showForwardIVs,
    showStats: showTermStructureIVStats,
    useSpotGammaIV,
    statsLookback: currentStatsLookback,
    hiddenDates,
    showDividends,
    showZoomBrush,
    markedExpirations,
    interpolateTooltipValues,
  } = config;

  // Visible lines = all selected date keys NOT in hiddenDates
  const visibleChartLines = useMemo(
    () =>
      termStructureSelectedDailyGreeks
        .map((dg) => dg.key)
        .filter((key) => !hiddenDates.includes(key)),
    [termStructureSelectedDailyGreeks, hiddenDates],
  );

  const { data: dividendsData } = useDividends({ sym: selectedSym });
  const { dividendDates, dividendAmounts } = useMemo(
    () => transformDividendsToRiskEngineFormat(dividendsData ?? []),
    [dividendsData],
  );

  const dividendEventsMap = useMemo(() => {
    const dividendMap = new Map<string, string[]>();
    if (dividendsData && dividendsData.length > 0) {
      dividendsData.forEach((div) => {
        if (div.date) {
          const dateStr = dayjs(div.date).format('YYYY-MM-DD');
          const amount = div.dividend ?? div.adj_dividend;
          const label = div.is_forecast ? 'Forecast' : 'Actual';
          const eventText = `Div: $${amount?.toFixed(2) ?? '0'} (${label})`;
          const existingEvents = dividendMap.get(dateStr) ?? [];
          existingEvents.push(eventText);
          dividendMap.set(dateStr, existingEvents);
        }
      });
    }
    return dividendMap;
  }, [dividendsData]);

  // Legs on the same date collapse into one marker; disagreeing colors use warning instead of two overlapping lines.
  const markedExpirationsByDate = useMemo(() => {
    const byDate = new Map<
      string,
      { label: string; color?: string; disagreement: boolean }
    >();
    markedExpirations.forEach(({ date, label, color }) => {
      const dateStr = dayjs(date).format('YYYY-MM-DD');
      const existing = byDate.get(dateStr);
      if (!existing) {
        byDate.set(dateStr, { label, color, disagreement: false });
      } else if (existing.color !== color) {
        existing.disagreement = true;
      }
    });
    const result = new Map<string, { label: string; color?: string }>();
    byDate.forEach(({ label, color, disagreement }, dateStr) => {
      result.set(dateStr, {
        label,
        color: disagreement ? theme.palette.warning.main : color,
      });
    });
    return result;
  }, [markedExpirations, theme]);

  const termStructureSharedKey: 'daysToExpiry' | 'expirationDate' = useMemo(
    () => getTermStructureKeyFromLabel(selectedDisplayType),
    [selectedDisplayType],
  );

  const { zoomChartConfig } = useBrushZoom<TermStructure>(
    zoomConfig,
    setZoomConfig,
    termStructureSharedKey,
    termStructureChartData,
  );

  const visibleGreeks = useMemo(() => {
    return termStructureSelectedDailyGreeks?.filter((dg: DailyGreeks) =>
      visibleChartLines.includes(dg.key),
    );
  }, [visibleChartLines, termStructureSelectedDailyGreeks]);

  useEffect(() => {
    const processData = async () => {
      let finalData: TermStructure[] = [];

      if (currentStats) {
        const pricesPerTradeDateMap = await getPricesPerTradeDateMap(
          termStructureSelectedDailyGreeks.map((dg) => dg.tradeDate),
          selectedSym,
          getClosingPrice,
        );

        termStructureSelectedDailyGreeks.forEach((datedGreeks) => {
          const key = datedGreeks.tradeDate.format('YYYY-MM-DD');

          const closestGreeksExp = findClosestGreeksExp(
            filteredGreeksMap,
            datedGreeks.tradeDate,
          );

          const fwdPrice: number = filteredGreeksMap
            ? findD50Price(filteredGreeksMap[closestGreeksExp])
            : 0.0;
          const currentPrice: number =
            pricesPerTradeDateMap.get(key) || fwdPrice;

          const updatedTermStructureData: TermStructure[] =
            resolveTermStructureForLine(
              datedGreeks,
              datedGreeks.data,
              datedGreeks.tradeDate,
              datedGreeks.key,
              currentStats[currentStatsLookback],
              currentPrice,
              hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
              dividendDates,
              dividendAmounts,
              exerciseStyles,
            ) ?? [];

          if (visibleChartLines.includes(datedGreeks.key)) {
            finalData = mergeObjectLists(
              finalData,
              updatedTermStructureData,
              termStructureSharedKey,
            );
          }
        });

        setTermStructureData(finalData);
      }
    };

    processData();
  }, [
    termStructureSelectedDailyGreeks,
    visibleChartLines,
    filteredGreeksMap,
    currentStatsLookback,
    exerciseStyles,
  ]);

  const handleResponse = useCallback(
    async (response: { data: RawGreeksDataMap }) => {
      if (response?.data != null) {
        const filteredGreeks = getFilteredGreeksData(response.data);
        setFilteredGreeksMap(filteredGreeks);

        const tradeDate = getQueryDate(true);
        const key = tradeDate.format('YYYY-MM-DD');
        setTermStructureSelectedDailyGreeks((currentItems: DailyGreeks[]) => {
          const index = currentItems.findIndex((item) => item.key === key);

          if (index === -1) {
            return currentItems;
          }

          const newItem = {
            ...currentItems[index],
            tradeDate,
            data: filteredGreeks,
          };

          return currentItems.map((item, idx) =>
            idx === index ? newItem : item,
          );
        });
      }
    },
    [setTermStructureSelectedDailyGreeks],
  );

  useEffect(() => {
    return poll(worker, {
      url: hasIVolAccess
        ? `v2/latest_greeks?sym=${encodeURIComponent(selectedSym)}`
        : `v2/free_latest_greeks?sym=${encodeURIComponent(selectedSym)}`,
      interval: 10_000,
      buffer: true,
      msgpack: true,
      onResponse: handleResponse,
    });
  }, [worker, hasIVolAccess, handleResponse, selectedSym]);

  // Initialize / re-fetch data when symbol changes or useSpotGammaIV toggles
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        let finalData: TermStructure[] = [];
        let updatedSelectedDailyGreeks: DailyGreeks[] = [];
        let usedColors: string[] = [];
        const queryDate = getQueryDate(true, currentTimezone);

        const rawGreeksPromises: Promise<RawGreeksDataMap | null>[] =
          termStructureSelectedDailyGreeks.map((dg: DailyGreeks) => {
            if (dg.precomputedTermStructure != null) {
              return Promise.resolve(null);
            }

            const tradeDate = dg.tradeDate.isSame(queryDate, 'day')
              ? queryDate
              : dg.tradeDate;
            const key: string = tradeDate.format('YYYY-MM-DD');

            return tradeDate.isSame(getQueryDate(true, currentTimezone), 'day')
              ? getCurrentGreeksData(selectedSym)
              : getDailyGreeksData(key, selectedSym);
          });

        const [stats, pricesPerTradeDateMap, ...greeks]: [
          RawStatsDataMap | null,
          Map<string, number>,
          ...(RawGreeksDataMap | null)[],
        ] = await Promise.all([
          getStatisticsData(selectedSym),
          getPricesPerTradeDateMap(
            termStructureSelectedDailyGreeks.map((dg) => dg.tradeDate),
            selectedSym,
            getClosingPrice,
          ),
          ...rawGreeksPromises,
        ]);

        termStructureSelectedDailyGreeks.forEach(
          (dg: DailyGreeks, index: number) => {
            const updatedRawGreeks: RawGreeksDataMap | null = greeks[index];
            const tradeDate = dg.tradeDate.isSame(queryDate, 'day')
              ? queryDate
              : dg.tradeDate;
            const key: string = tradeDate.format('YYYY-MM-DD');
            const closestGreeksExp = findClosestGreeksExp(
              filteredGreeksMap,
              dg.tradeDate,
            );

            const fwdPrice: number = filteredGreeksMap
              ? findD50Price(filteredGreeksMap[closestGreeksExp])
              : 0.0;
            const currentPrice: number =
              pricesPerTradeDateMap.get(key) || fwdPrice;

            const transformedTermStructureData: TermStructure[] | null =
              resolveTermStructureForLine(
                dg,
                updatedRawGreeks,
                tradeDate,
                key,
                stats?.[currentStatsLookback],
                currentPrice,
                hasDashAccess(dashAccess, DashAccess.INTERNAL) &&
                  useSpotGammaIV,
                dividendDates,
                dividendAmounts,
                exerciseStyles,
              );

            if (transformedTermStructureData) {
              const color =
                dg.color !== ''
                  ? dg.color
                  : (getNextAvailableColor(
                      termStructureUsedLineColors,
                      availableColors,
                    ) ?? theme.palette.primary.main);

              if (!hiddenDates.includes(key)) {
                finalData = mergeObjectLists(
                  finalData,
                  transformedTermStructureData,
                  termStructureSharedKey,
                );
              }

              usedColors = [...usedColors, color];
              updatedSelectedDailyGreeks = [
                ...updatedSelectedDailyGreeks,
                {
                  tradeDate,
                  key,
                  color,
                  currentPrice,
                  data: updatedRawGreeks ?? {},
                  precomputedTermStructure: dg.precomputedTermStructure,
                  legendLabel: dg.legendLabel,
                },
              ];
            }
          },
        );

        setCurrentStats(stats);
        setTermStructureUsedLineColors(usedColors);
        setTermStructureSelectedDailyGreeks(updatedSelectedDailyGreeks);
        setTermStructureData(finalData);

        // Precomputed lines are caller-supplied every render, not restored from URL - drop them here too.
        const additionalEntries = updatedSelectedDailyGreeks
          .filter(
            (dg) =>
              !dg.tradeDate.isSame(queryDate, 'day') &&
              dg.precomputedTermStructure == null,
          )
          .map(({ data: _data, ...rest }) => rest);
        onConfigChange({ additionalDates: additionalEntries });
      } catch (err) {
        console.error('Error fetching term structure data:', err);
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to fetch term structure data',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedSym, useSpotGammaIV, exerciseStyles]);

  useEffect(() => {
    const getCalendarData = async () => {
      try {
        let calendarMap: Map<string, string[]> = new Map();

        if (visibleGreeks?.length > 0) {
          const minSnapshotTime = Math.min(
            ...visibleGreeks.map((tsGreeks: DailyGreeks) =>
              getSnapshotTime(tsGreeks.data),
            ),
          );
          if (ETFSymbols.has(selectedSym)) {
            const macroCalendarData: EconomicCalendarData[] =
              await getMacroCalendar(
                dayjs(minSnapshotTime).subtract(1, 'day').format('YYYY-MM-DD'),
                dayjs(minSnapshotTime).add(30, 'day').format('YYYY-MM-DD'),
              );
            const highPriUSData = macroCalendarData.filter(
              (calendarData: any) =>
                calendarData.impact === 'High' && calendarData.country === 'US',
            );
            calendarMap = highPriUSData.reduce((acc, obj) => {
              const dateStr = obj.date.split(' ')[0];
              const existingEvents = acc.get(dateStr) ?? [];
              acc.set(dateStr, existingEvents.concat([obj.event]));

              return acc;
            }, new Map<string, string[]>());
          } else {
            const startDate = dayjs(minSnapshotTime);
            const endDate = businessDaysAdd(getQueryDate(true), 365);
            const earningsCalendarIVol: Earnings[] = await getEarningsForSyms(
              [selectedSym],
              startDate,
              endDate,
            );

            earningsCalendarIVol.forEach((earningsObj: Earnings) => {
              const dateStr = dayjs(earningsObj.day).format('YYYY-MM-DD');
              const existingEvents: string[] = calendarMap.get(dateStr) ?? [];
              existingEvents.unshift(`${earningsObj.sym} Earnings`);
              calendarMap.set(dateStr, existingEvents);
            });
          }
        }

        setUsEconomicEvents(calendarMap);
      } catch (err) {
        console.error('Error fetching calendar data:', err);
        // Don't set error state for calendar data as it's not critical
        setUsEconomicEvents(new Map());
      }
    };

    getCalendarData();
  }, [
    getEarningsForSyms,
    getMacroCalendar,
    selectedSym,
    termStructureSelectedDailyGreeks,
    visibleGreeks,
    setUsEconomicEvents,
  ]);

  useEffect(() => {
    if (termStructureData) {
      const sortedData: TermStructure[] = [...termStructureData]
        .map((ts: TermStructure) => ({
          ...ts,
          expirationDate: roundToStartOfDay(ts.expirationDate),
        }))
        .sort(
          (ts1: TermStructure, ts2: TermStructure) =>
            ts1[termStructureSharedKey] - ts2[termStructureSharedKey],
        )
        .filter(
          (
            termStructure: TermStructure,
            index: number,
            self: TermStructure[],
          ) =>
            self[index - 1]?.[termStructureSharedKey] !==
            termStructure[termStructureSharedKey],
        );

      const minSnapshotTime = Math.min(
        ...visibleGreeks.map((tsGreeks: DailyGreeks) =>
          getSnapshotTime(tsGreeks.data),
        ),
      );
      const econDates: string[] = Array.from(usEconomicEvents?.keys() ?? [])
        .filter((date) =>
          isWithinSnapshotToFirstExp(minSnapshotTime, date, sortedData ?? []),
        )
        .sort();

      const dividendDates: string[] = Array.from(
        dividendEventsMap?.keys() ?? [],
      )
        .filter((date) =>
          isWithinSnapshotToFirstExp(minSnapshotTime, date, sortedData ?? []),
        )
        .sort();

      if (
        sortedData.length > 0 &&
        termStructureSharedKey === 'expirationDate' &&
        showEconomicEvents &&
        econDates.length > 0
      ) {
        const firstExp = dayjs(sortedData[0].expirationDate);
        const diffDays = firstExp.diff(econDates[0], 'day');

        sortedData.unshift({
          expirationDate:
            sortedData[0][termStructureSharedKey] - (diffDays + 1) * DAY_IN_MS,
          daysToExpiry: 0,
        });
      }

      if (
        sortedData.length > 0 &&
        termStructureSharedKey === 'expirationDate' &&
        hasDashAccess(dashAccess, DashAccess.INTERNAL) &&
        showDividends &&
        dividendDates.length > 0
      ) {
        const firstExp = dayjs(sortedData[0].expirationDate);
        const firstDividendDate = dividendDates[0];
        const diffDays = firstExp.diff(firstDividendDate, 'day');

        if (diffDays > 0) {
          sortedData.unshift({
            expirationDate:
              sortedData[0][termStructureSharedKey] -
              (diffDays + 1) * DAY_IN_MS,
            daysToExpiry: 0,
          });
        }
      }

      // chartData and zoomConfig.data both need the same interpolated rows, or the tooltip reads stale data.
      const finalData = interpolateTooltipValues
        ? interpolateMissingSeriesValues(
            sortedData,
            termStructureSharedKey,
            visibleChartLines,
          )
        : sortedData;

      setChartData(finalData);

      // 90-day default only applies when the brush is visible to widen it back out.
      const initialRightIdx =
        (!zoomConfig.data ||
          zoomConfig.data.length === 0 ||
          !zoomConfig.rightIdx) &&
        showZoomBrush
          ? closestExpirationIdx(
              finalData,
              termStructureSelectedDailyGreeks,
              90,
            )
          : undefined;
      updateBrushZoomConfig(
        zoomConfig,
        finalData,
        setZoomConfig,
        initialRightIdx,
      );
    }
  }, [
    termStructureSelectedDailyGreeks,
    setChartData,
    setZoomConfig,
    termStructureData,
    termStructureSharedKey,
    showEconomicEvents,
    showDividends,
    dashAccess,
    usEconomicEvents,
    dividendEventsMap,
    interpolateTooltipValues,
    visibleChartLines,
  ]);

  const isWithinSnapshotToFirstExp = (
    snapshotTime: number,
    eventDate: string,
    sortedData: TermStructure[],
  ): boolean => {
    if (sortedData.length === 0) {
      return false;
    }
    const eventDateObj = dayjs(eventDate);
    const snapshot = dayjs(snapshotTime);
    const firstExp = dayjs(sortedData[0].expirationDate);
    return eventDateObj.isAfter(snapshot) && eventDateObj.isBefore(firstExp);
  };

  const isWithinChartRange = (date: string): boolean => {
    if (termStructureChartData.length === 0) {
      return false;
    }
    const dateObj = dayjs(date);
    const minDate = dayjs(termStructureChartData[0].expirationDate);
    const maxDate = dayjs(
      termStructureChartData[termStructureChartData.length - 1].expirationDate,
    );
    return (
      (dateObj.isSame(minDate, 'day') || dateObj.isAfter(minDate, 'day')) &&
      (dateObj.isSame(maxDate, 'day') || dateObj.isBefore(maxDate, 'day'))
    );
  };

  // Same visual treatment as VolSkewChart's marked-strike labels.
  const RotatedVerticalLabel = ({
    viewBox,
    value,
    backgroundColor = theme.palette.background.paper,
    textColor = theme.palette.warning.main,
    fontSize = 13,
    fontWeight = 500,
    topOffset = 40,
  }: any) => {
    const { x, width } = viewBox;
    const padding = 10;
    const estimatedTextLength = value.length * (fontSize * 0.6);
    const rectHeight = fontSize + padding;
    const rectWidth = estimatedTextLength + padding;
    const centerX = x + width / 2;
    const adjustedY = topOffset + rectHeight / 2;

    return (
      <g>
        <rect
          x={centerX - rectWidth / 2}
          y={adjustedY - rectHeight / 2}
          width={rectWidth}
          height={rectHeight}
          fill={backgroundColor}
          rx={5}
          transform={`rotate(-90, ${centerX}, ${adjustedY})`}
        />
        <text
          x={centerX}
          y={adjustedY}
          fill={textColor}
          fontSize={fontSize}
          fontWeight={fontWeight}
          letterSpacing={1}
          textAnchor="middle"
          dominantBaseline="central"
          transform={`rotate(-90, ${centerX}, ${adjustedY})`}
        >
          {value}
        </text>
      </g>
    );
  };

  const renderMarkedDateLine = (
    keyPrefix: string,
    date: string,
    x: number,
    label: string,
    ifOverflow: 'visible' | 'extendDomain' = 'visible',
  ) => (
    <ReferenceLine
      key={`${keyPrefix}-${date}`}
      x={x}
      isFront
      stroke="transparent"
      ifOverflow={ifOverflow}
      yAxisId="left"
    >
      <Label
        position="insideBottom"
        angle={-90}
        style={{
          textAnchor: 'start',
          fontSize: isMobile ? 10 : 13,
          fontWeight: 500,
          userSelect: 'none',
        }}
      >
        {label}
      </Label>
    </ReferenceLine>
  );

  const ticks = useMemo(() => {
    return getTicksBrushed(
      termStructureChartData.map(
        (termStructure: TermStructure) => termStructure[termStructureSharedKey],
      ),
      zoomConfig,
      termStructureSharedKey === 'expirationDate'
        ? TIMESTAMP_TICK_CONFIG
        : DTE_TICK_CONFIG,
    )?.map((tick: number) =>
      termStructureSharedKey === 'expirationDate'
        ? roundToStartOfDay(tick)
        : tick,
    );
  }, [termStructureChartData, termStructureSharedKey, zoomConfig]);

  const visibleSelectedDailyGreeks = useMemo(
    () =>
      termStructureSelectedDailyGreeks.filter((greeks: DailyGreeks) =>
        visibleChartLines.includes(greeks.key),
      ),
    [termStructureSelectedDailyGreeks, visibleChartLines],
  );

  const chartTooltipShadow = `${theme.palette.background.paper} 1px 1px 4px, ${theme.palette.background.paper} 1px -1px 4px, ${theme.palette.background.paper} -1px 1px 4px, ${theme.palette.background.paper} -1px -1px 4px, ${theme.palette.background.paper} 2px 2px 4px, ${theme.palette.background.paper} 2px -2px 4px, ${theme.palette.background.paper} -2px 2px 4px, ${theme.palette.background.paper} 2px -2px 4px`;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const row = payload[0]?.payload;
      return (
        <Paper
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '12px',
            gap: '6px',
            color: theme.palette.text.primary,
            border: 'none',
            backgroundColor: alpha(theme.palette.background.paper, 0.85),
            boxShadow: theme.palette.shadows.paperBoxShadow,
            width: '275px',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography sx={{ fontSize: '12px' }}>
              {selectedDisplayType}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              {selectedDisplayType === ExpirationsDisplayType.ExpirationDate
                ? getFormattedDateFromUTC(label)
                : label}
            </Typography>
          </Stack>
          {/* The other axis' value, always shown alongside whichever one is primary above */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography sx={{ fontSize: '12px', color: theme.palette.gray }}>
              {selectedDisplayType === ExpirationsDisplayType.ExpirationDate
                ? ExpirationsDisplayType.DaysToExpiration
                : ExpirationsDisplayType.ExpirationDate}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: theme.palette.gray,
              }}
            >
              {selectedDisplayType === ExpirationsDisplayType.ExpirationDate
                ? row?.daysToExpiry
                : getFormattedDateFromUTC(row?.expirationDate)}
            </Typography>
          </Stack>
          {selectedDisplayType === ExpirationsDisplayType.ExpirationDate &&
            showEconomicEvents &&
            usEconomicEvents?.has(getFormattedDateFromUTC(label)) && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    width: '50%',
                    color: theme.palette.gray,
                  }}
                >
                  Key Event
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    textAlign: 'end',
                    color: theme.palette.gray,
                  }}
                >
                  {usEconomicEvents.get(getFormattedDateFromUTC(label))?.[0]}
                </Typography>
              </Stack>
            )}
          {selectedDisplayType === ExpirationsDisplayType.ExpirationDate &&
            hasDashAccess(dashAccess, DashAccess.INTERNAL) &&
            showDividends &&
            dividendEventsMap?.has(getFormattedDateFromUTC(label)) && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    width: '50%',
                    color: theme.palette.warning.main,
                  }}
                >
                  Dividend
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    textAlign: 'end',
                    color: theme.palette.warning.main,
                  }}
                >
                  {dividendEventsMap.get(getFormattedDateFromUTC(label))?.[0]}
                </Typography>
              </Stack>
            )}

          {payload.map((payloadObj: any, index: number) => {
            if (payloadObj.name === 'Bounds') {
              return (
                <Stack
                  width="100%"
                  key={`${payloadObj.value[0]}-${payloadObj.value[1]}-${index}`}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Typography
                      sx={{
                        color: payloadObj.color,
                        fontSize: '13px',
                        textShadow: chartTooltipShadow,
                      }}
                    >
                      10th pct
                    </Typography>
                    <Typography
                      sx={{
                        color: payloadObj.color,
                        fontSize: '13px',
                        fontWeight: 'bold',
                        textShadow: chartTooltipShadow,
                      }}
                    >
                      {formatAsPercentage(payloadObj.value[0])}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Typography
                      sx={{
                        color: payloadObj.color,
                        fontSize: '13px',
                        textShadow: chartTooltipShadow,
                      }}
                    >
                      90th pct
                    </Typography>
                    <Typography
                      sx={{
                        color: payloadObj.color,
                        fontSize: '13px',
                        fontWeight: 'bold',
                        textShadow: chartTooltipShadow,
                      }}
                    >
                      {formatAsPercentage(payloadObj.value[1])}
                    </Typography>
                  </Stack>
                </Stack>
              );
            } else {
              return (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  key={`${payloadObj.name}-${payloadObj.value}-${index}`}
                >
                  <Typography
                    sx={{
                      color: payloadObj.color,
                      fontSize: '13px',
                      textShadow: chartTooltipShadow,
                    }}
                  >
                    {payloadObj.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: payloadObj.color,
                      fontSize: '13px',
                      fontWeight: 'bold',
                      textShadow: chartTooltipShadow,
                    }}
                  >
                    {formatAsPercentage(payloadObj.value)}
                  </Typography>
                </Stack>
              );
            }
          })}
        </Paper>
      );
    }

    return null;
  };

  if (!selectedSym || loading || error != null) {
    return <Loader isLoading={loading} />;
  }

  if (termStructureChartData.length === 0 && !loading && error != null) {
    return (
      <ErrorContent content="Failed to retrieve the data needed for Term Structure Chart. Please either refresh the page or contact us!" />
    );
  }

  return (
    <ChartWatermarkContainer
      ref={chartRef}
      style={{ flex: 1, position: 'relative' }}
      size={20}
      offsetX={50}
      offsetY={55}
      sym={selectedSym}
    >
      {!hasIVolAccess && isMobile && (
        <Stack alignItems="center">
          <Typography sx={{ color: theme.palette.primary.main, fontSize: 14 }}>
            Access limited to <strong>{selectedSym}</strong>
          </Typography>
        </Stack>
      )}

      <ResponsiveContainer>
        <ComposedChart
          ref={rechartsRef}
          margin={{
            ...DEFAULT_CHART_MARGINS,
            bottom: isMobile ? 40 : 20,
            left: 15,
            right: 30,
          }}
          {...zoomChartConfig}
        >
          <CartesianGrid strokeDasharray="1 10" stroke={theme.palette.gray} />

          <XAxis
            allowDataOverflow
            label={{
              value: selectedDisplayType,
              fontSize: 12,
              offset: 3,
              position: 'insideBottom',
              fontWeight: 600,
            }}
            dataKey={termStructureSharedKey}
            name={selectedDisplayType}
            domain={['dataMin', 'dataMax']}
            tick={{ fontSize: 10 }}
            type="number"
            tickFormatter={(val) =>
              selectedDisplayType === ExpirationsDisplayType.ExpirationDate
                ? getFormattedDateFromUTC(val)
                : val
            }
            ticks={ticks}
            tickCount={20}
          />
          {showZoomBrush && (
            <Brush
              dataKey={termStructureSharedKey}
              tickFormatter={(val) =>
                selectedDisplayType === ExpirationsDisplayType.ExpirationDate
                  ? getFormattedDateFromUTC(val)
                  : val
              }
              startIndex={zoomConfig.leftIdx}
              endIndex={zoomConfig.rightIdx}
              onChange={(brushIndices: any) =>
                setZoomConfig((prev) => ({
                  ...prev,
                  leftIdx: brushIndices.startIndex,
                  rightIdx: brushIndices.endIndex,
                }))
              }
              height={25}
              travellerWidth={15}
              stroke={theme.palette.gray}
              fill={theme.palette.background.paper}
              alwaysShowText
            />
          )}
          <YAxis
            allowDataOverflow
            yAxisId="left"
            domain={[
              (dataMin: number) => Math.max(dataMin - 0.03, 0),
              (dataMax: number) => dataMax + 0.03,
            ]}
            tick={{ fontSize: 11 }}
            type="number"
            label={{
              ...DEFAULT_Y_AXIS_STYLES,
              offset: 0,
              value: 'Implied Volatility',
            }}
            tickFormatter={(value: number) => formatAsPercentage(value)}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            wrapperStyle={{ fontSize: '12px' }}
            height={isMobile ? 60 : 40}
          />

          {visibleSelectedDailyGreeks.length > 0 && (
            <Area
              yAxisId="left"
              legendType="none"
              name="Bounds"
              dataKey={`bounds-${
                visibleSelectedDailyGreeks.reduce((smallest, current) => {
                  return smallest.tradeDate < current.tradeDate
                    ? smallest
                    : current;
                }).key
              }`}
              stroke="none"
              fillOpacity={0.3}
              fill="#8884d8"
              connectNulls
              hide={!showTermStructureIVStats}
            />
          )}

          {visibleSelectedDailyGreeks.map((dailyGreeks: DailyGreeks) => {
            const tradeDate = dailyGreeks.tradeDate;
            const dateStr = dailyGreeks.key;
            const stroke = dailyGreeks.color;
            const fivStroke = alpha(stroke, 0.4);
            const name =
              dailyGreeks.legendLabel ??
              getTradeDateDisplayText(tradeDate, currentTimezone);
            return (
              <Fragment key={dateStr}>
                <Line
                  yAxisId="left"
                  type="linear"
                  dataKey={dateStr}
                  name={name}
                  stroke={stroke}
                  dot={(dotProps: any) =>
                    dotProps.payload?.[interpolatedFlagKey(dateStr)] ? (
                      <Fragment key={dotProps.key} />
                    ) : (
                      <Dot
                        key={dotProps.key}
                        cx={dotProps.cx}
                        cy={dotProps.cy}
                        r={3}
                        stroke={stroke}
                        strokeWidth={2}
                        fill={stroke}
                      />
                    )
                  }
                  strokeWidth={3}
                  connectNulls
                />
                {showForwardIVs && (
                  <Line
                    key={`${dateStr} FIV`}
                    yAxisId="left"
                    type="linear"
                    dataKey={`${dateStr}/FIV`}
                    name={`${name} FIV`}
                    stroke={fivStroke}
                    dot={{ stroke, strokeWidth: 2 }}
                    strokeWidth={3}
                    connectNulls
                  />
                )}
              </Fragment>
            );
          })}

          {selectedDisplayType === ExpirationsDisplayType.ExpirationDate &&
            showEconomicEvents &&
            Array.from(usEconomicEvents?.entries() ?? []).map(
              ([date, events]: [string, string[]]) =>
                isWithinChartRange(date)
                  ? renderMarkedDateLine(
                      'econ',
                      date,
                      dayjs(date).valueOf(),
                      events[0],
                      'visible',
                    )
                  : null,
            )}
          {selectedDisplayType === ExpirationsDisplayType.ExpirationDate &&
            hasDashAccess(dashAccess, DashAccess.INTERNAL) &&
            showDividends &&
            Array.from(dividendEventsMap?.entries() ?? []).map(
              ([date, events]: [string, string[]]) => {
                const xValue = dayjs(date).valueOf();
                const minX = termStructureChartData[0]?.expirationDate ?? 0;
                const maxX =
                  termStructureChartData[termStructureChartData.length - 1]
                    ?.expirationDate ?? 0;
                if (
                  !isWithinChartRange(date) ||
                  xValue <= minX ||
                  xValue > maxX
                ) {
                  return null;
                }
                return renderMarkedDateLine(
                  'div',
                  date,
                  xValue,
                  events[0],
                  'extendDomain',
                );
              },
            )}
          {Array.from(markedExpirationsByDate.entries())
            // Always shown, extending the axis if needed - the Current/Projected lines still stop at their real data.
            .map(([dateStr, { label, color }], i) => {
              const x =
                termStructureSharedKey === 'expirationDate'
                  ? dayjs(dateStr).valueOf()
                  : dayjs(dateStr)
                      .startOf('day')
                      .diff(dayjs().startOf('day'), 'day');
              const markerColor = color ?? theme.palette.warning.main;
              return (
                <ReferenceLine
                  key={`exp-${dateStr}`}
                  x={x}
                  isFront
                  stroke={markerColor}
                  ifOverflow="extendDomain"
                  yAxisId="left"
                  style={{ opacity: 0.75 }}
                  strokeDasharray="8 8"
                  label={
                    <RotatedVerticalLabel
                      value={label}
                      backgroundColor={alpha(markerColor, 0.15)}
                      textColor={markerColor}
                      fontSize={isMobile ? 10 : 13}
                      fontWeight={600}
                      topOffset={40 + i * 40}
                    />
                  }
                />
              );
            })}
          {getZoomConfigRefArea(zoomConfig, 'left')}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartWatermarkContainer>
  );
};

export default TermStructureChart;
